import {
  Divider,
  EHeadingStyleVariant,
  ELinkVariant,
  EPaginationVariant,
  ETextStyleVariant,
  Heading,
  Link,
  Pagination,
  Text,
} from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import MaxWidth from '@/components/common/layout/MaxWidth';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import formatDate from '@/utility/format-date';

import { useEarnedCredits } from './data';

const ROWS_PER_PAGE = 20;

export const CreditsBalance = () => {
  const intl = useIntl();
  const { isMobile } = useBreakpoint();

  const { data } = useEarnedCredits();
  const [page, setPage] = useState(1);

  if (!data?.logs.length) return null;

  const currentPageStartIndex = (page - 1) * ROWS_PER_PAGE;
  const currentPageEndIndex = currentPageStartIndex + ROWS_PER_PAGE;
  const currentPageLogs = data.logs.slice(currentPageStartIndex, currentPageEndIndex);
  const shouldShowPagination = data.logs.length > ROWS_PER_PAGE;
  const numberOfPages = Math.ceil(data.logs.length / ROWS_PER_PAGE);

  return (
    <MaxWidth className="flex flex-col py-10 m-auto gap-6 md:gap-10">
      <div className="flex flex-col gap-6">
        <Heading variant={EHeadingStyleVariant.H4} className="text-center">
          {intl.formatMessage({ defaultMessage: 'Travel credit balance', id: 'r1Z78K' })}
        </Heading>

        {(isMobile && (
          <div>
            {currentPageLogs.map((log, index) => {
              return (
                <div key={index} className="flex flex-col my-4 gap-4">
                  <Text variant={ETextStyleVariant.LargeRegular} className="text-gray-50">
                    {formatDate(log.created)}
                  </Text>

                  <div className="flex gap-4">
                    <div className="flex flex-col overflow-hidden gap-4 grow">
                      {log.booking_id && (
                        <Link
                          target="_blank"
                          rel="noreferrer"
                          href={log.booking_link || `/trips/${log.booking_id}`}
                          variant={ELinkVariant.Underline}
                          className="truncate">
                          {intl.formatMessage(
                            { defaultMessage: 'Booking ID: {bookingId}', id: 'wxc/RN' },
                            { bookingId: log.booking_id },
                          )}
                        </Link>
                      )}

                      {log.referee_email && (
                        <Text variant={ETextStyleVariant.LargeRegular} className="truncate">
                          {log.referee_email}
                        </Text>
                      )}

                      <Text variant={ETextStyleVariant.Label} className="text-gray-50">
                        {log.type}
                      </Text>
                    </div>

                    <Text variant={ETextStyleVariant.LargeBold} className="text-gray-50">
                      {intl.formatNumber(log.amount / 100, {
                        signDisplay: 'always',
                        style: 'currency',
                        currency: 'USD',
                      })}
                    </Text>
                  </div>

                  <Divider />
                </div>
              );
            })}

            <div className="flex my-4 gap-4">
              <Text variant={ETextStyleVariant.LargeBold} className="text-gray-50 grow">
                {intl.formatMessage({ defaultMessage: 'Total balance', id: 'krwBAI' })}
              </Text>

              <Heading variant={EHeadingStyleVariant.H5} className="text-gray-50">
                {intl.formatNumber(data.available_credits / 100, {
                  style: 'currency',
                  currency: 'USD',
                })}
              </Heading>
            </div>
          </div>
        )) || (
          <div>
            <table className="w-full">
              {currentPageLogs.map((log, index) => {
                return (
                  <tr key={index} className="border-b border-neutral-20">
                    <td className="py-4 pr-3 whitespace-nowrap">
                      <Text variant={ETextStyleVariant.LargeRegular} className="text-gray-50">
                        {formatDate(log.created)}
                      </Text>
                    </td>

                    <td className="w-full px-6 py-4 max-w-[0px]">
                      <div className="flex items-center gap-4">
                        {log.booking_id && (
                          <Link
                            target="_blank"
                            rel="noreferrer"
                            href={log.booking_link || `/trips/${log.booking_id}`}
                            variant={ELinkVariant.NoUnderline}
                            className="truncate">
                            {intl.formatMessage(
                              { defaultMessage: 'Booking ID: {bookingId}', id: 'wxc/RN' },
                              { bookingId: log.booking_id },
                            )}
                          </Link>
                        )}

                        {log.referee_email && (
                          <Text variant={ETextStyleVariant.LargeRegular} className="truncate">
                            {log.referee_email}
                          </Text>
                        )}

                        <Text variant={ETextStyleVariant.Label} className="text-gray-50">
                          {log.type}
                        </Text>
                      </div>
                    </td>

                    <td className="py-4 pl-3 text-right whitespace-nowrap">
                      <Text variant={ETextStyleVariant.LargeBold}>
                        {intl.formatNumber(log.amount / 100, {
                          signDisplay: 'always',
                          style: 'currency',
                          currency: 'USD',
                        })}
                      </Text>
                    </td>
                  </tr>
                );
              })}

              <tr>
                <td colSpan={2} className="w-full py-4 pr-3">
                  <Text variant={ETextStyleVariant.LargeBold} className="text-gray-50">
                    {intl.formatMessage({ defaultMessage: 'Total balance', id: 'krwBAI' })}
                  </Text>
                </td>

                <td className="py-4 pl-3 text-right whitespace-nowrap">
                  <Heading variant={EHeadingStyleVariant.H5} className="text-gray-50">
                    {intl.formatNumber(data.available_credits / 100, {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </Heading>
                </td>
              </tr>
            </table>
          </div>
        )}

        {shouldShowPagination && (
          <div className="flex justify-center gap-2">
            <Pagination
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Credits balance pagination',
                id: 'S1dZ+J',
              })}
              previousPageAriaLabel={intl.formatMessage({
                defaultMessage: 'Previous',
                id: 'JJNc3c',
              })}
              nextPageAriaLabel={intl.formatMessage({ defaultMessage: 'Next', id: '9+Ddtu' })}
              variant={EPaginationVariant.PageNumbers}
              pageNumber={page}
              totalPages={numberOfPages}
              pretext={intl.formatMessage({ defaultMessage: 'of', id: 'C9WGEu' })}
              onChange={setPage}
            />
          </div>
        )}
      </div>
    </MaxWidth>
  );
};
